import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {TrustAccountListEmpty} from '@/features/trust-accounts/components';
import {groupByTrustStatus} from '@/features/trust-accounts/utils';

import {GuestAccountsListSkeleton} from './GuestAccountsList.skeleton';
import {GuestFirmCard} from './GuestFirmCard';
import {
  useGuestAccountColumns,
  useGuestCollapsedFirms,
  useGuestCollapsedStatusGroups
} from '../hooks';
import {groupGuestAccountsByFirm, toGuestListItem} from '../utils';

import type {GuestSharedTrustAccountDTO} from '@sollapay/types';
import type {ListOnSortingChange, ListSortingState, ListView} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface GuestAccountsListProps {
  accounts?: GuestSharedTrustAccountDTO[];
  view?: ListView;
  sorting?: ListSortingState;
  onSortingChange?: ListOnSortingChange;
}

/** Shows a skeleton while `accounts` is still loading (undefined). */
export const GuestAccountsList: FC<GuestAccountsListProps> = ({
  accounts,
  view = 'table',
  sorting,
  onSortingChange
}) => {
  const {t} = useTranslation('guest');
  const firms = useMemo(
    () =>
      groupGuestAccountsByFirm(accounts ?? []).map(group => ({
        group,
        statusGroups: groupByTrustStatus(group.accounts.map(toGuestListItem))
      })),
    [accounts]
  );
  const {isCollapsed, toggle} = useGuestCollapsedFirms();
  const getStatusGroupFold = useGuestCollapsedStatusGroups();
  const columns = useGuestAccountColumns();

  if (!accounts) return <GuestAccountsListSkeleton view={view} />;

  if (accounts.length === 0) {
    return (
      <TrustAccountListEmpty
        title={t('accounts.empty.title')}
        subtitle={t('accounts.empty.description')}
      />
    );
  }

  return (
    <div data-slot="guest-accounts-list" className="flex flex-col gap-5">
      {firms.map(({group, statusGroups}) => (
        <GuestFirmCard
          key={group.firm.id}
          group={group}
          statusGroups={statusGroups}
          columns={columns}
          view={view}
          sorting={sorting}
          onSortingChange={onSortingChange}
          isCollapsed={isCollapsed(group.firm.id)}
          onToggle={toggle}
          groupFold={getStatusGroupFold(group.firm.id)}
        />
      ))}
    </div>
  );
};
